/* eslint-disable no-unused-vars */
/* eslint-disable indent */
/* eslint-disable */
import React, { useState, useEffect } from "react";
import { Card, Row, Col, message, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    TableComponent
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { ReportsService } from "../../../../services/api/reports";
import { downloadIncomingOutgoingShipmentDetailsReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";
import { useNavigate, useLocation } from "react-router-dom";
import { showErrorMessage } from "../../../../utils";

const IncomingOutGoingShipmentDetails = () => {
    // Global request cancellation hook
    const { createNewRequest, isLatestRequest } = useRequestCancellation();
    
    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    
    const [hubUser, setHubUser] = useState();
    
    const navigate = useNavigate();
    const location = useLocation();

    // Set user role
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo"));
        setHubUser(user?.roles?.[0]);
    }, []);

    // Fetch report data
    useEffect(() => {
        if (!hubUser) return;
        if (!location.state) return;
        const { bagBarcodeNo } = location.state;
        if (!bagBarcodeNo) return;
        fetchReport();
        // eslint-disable-next-line
    }, [hubUser, location.state, pagination.current, pagination.pageSize]);

    const fetchReport = async () => {
        // 🔥 Create new request for cancellation support
        const requestId = createNewRequest();
        
        setLoading(true);
        let shouldUpdateState = true;
        
        try {
            // Get form parameters from location.state
            const formParams = location.state || {};
            const { bagBarcodeNo } = formParams;
            if (!bagBarcodeNo) {
                setLoading(false);
                return;
            }
            
            setReportData([]);

            const params = {
                bagBarcodeNo: bagBarcodeNo,
                page: (pagination.current - 1),
                size: pagination.pageSize
            };

            const { data, total } = await ReportsService.getIncomingOutgoingShipmentDetails(params);
            
            // 🔥 Check if this is still the latest request
            if (!isLatestRequest(requestId.requestId)) {
                shouldUpdateState = false;
            } else {
                setReportData(data || []);
                setPagination(prev => ({
                    ...prev,
                    total: total || 0,
                }));
            }
        } catch (error) {
            console.error("API error:", error);
            // 🔥 Check if this is still the latest request
            if (!isLatestRequest(requestId.requestId)) {
                shouldUpdateState = false;
            } else {
                showErrorMessage("Failed to fetch incoming outgoing shipment details data");
                setReportData([]);
                setPagination(prev => ({
                    ...prev,
                    total: 0
                }));
            }
        } finally {
            // 🔥 Check if this is still the latest request
            if (isLatestRequest(requestId.requestId)) {
                setLoading(false);
            }
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const handleDownload = async () => {
        // Get form parameters from location.state
        const formParams = location.state || {};
        const { bagBarcodeNo } = formParams;
        
        if (!bagBarcodeNo) {
            showErrorMessage("Bag barcode number is missing for download");
            return;
        }
        
        setDownloadLoading(true);
        try {
            const downloadParams = {
                bagBarcodeNo: bagBarcodeNo
            };

            await downloadIncomingOutgoingShipmentDetailsReport(downloadParams);
            
            message.success("Incoming outgoing shipment details report downloaded successfully");
        } catch (error) {
            console.error("Download error:", error);
            message.error("Failed to download incoming outgoing shipment details report");
        } finally {
            setDownloadLoading(false);
        }
    };
    // Function to get table columns for incoming outgoing shipment details
    const getTableColumns = () => {
        return [
            { 
                title: "Bag No", 
                dataIndex: "bagNo", 
                key: "bagNo", 
                sorter: true, 
                align: "center",
                width: 150,
                render: (text, record) => (
                    <span
                        style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => {
                            const url = `/shipment-tracker?awb=${text}`;
                            window.open(url, "_blank");
                        }}
                    >
                        {text}
                    </span>
                )
            },
            { 
                title: "Shipment No", 
                dataIndex: "shipmentNo", 
                key: "shipmentNo", 
                sorter: true, 
                align: "center",
                width: 150,
                render: (text, record) => (
                    <span
                        style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => {
                            const url = `/shipment-tracker?awb=${text}`;
                            window.open(url, "_blank");
                        }}
                    >
                        {text}
                    </span>
                )
            },
            { 
                title: "Last Operation Time", 
                dataIndex: "lastOperationTime", 
                key: "lastOperationTime", 
                sorter: true, 
                align: "center",
                width: 200,
                render: (text) => moment(text).format("YYYY-MM-DD HH:mm:ss")
            },
            { 
                title: "Last Operation", 
                dataIndex: "lastOperation", 
                key: "lastOperation", 
                sorter: true, 
                align: "center",
                width: 150
            },
            { 
                title: "Last Location", 
                dataIndex: "lastLocation", 
                key: "lastLocation", 
                sorter: true, 
                align: "center",
                width: 200
            }
        ];
    };

    return (
        <div className="report-container">
            <Heading 
                title={`${location.state.type} bag Shipment Details Report`} 
                navigateTo={location.state.url}
                showBackButton={true}
            />
            <Card className="report-card">
                <Row style={{ marginTop: 16 }}>
                    <Col span={24} style={{ textAlign: "right" }}>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            loading={downloadLoading}
                            onClick={handleDownload}
                        >
                            Download Report
                        </Button>
                    </Col>
                </Row>
            </Card>
            <TableComponent
                columns={getTableColumns()}
                dataSource={reportData}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                rowKey={(record, index) => record.shipmentNo || record.bagNo || index}
                style={{ marginTop: 32 }}
            />
        </div>
    );
};

export default IncomingOutGoingShipmentDetails;
